import { PBTApiEndpoints } from './PBTConstants';

export interface PauseCaseRequest {
  reason: string;
  reasonText?: string;
}

export interface CancelCaseRequest {
  reason: string;
  reasonText?: string;
}

export interface CaseActionResponse {
  case: Record<string, unknown>;
  warnings: string[];
}

/**
 * Service for PBT case pause/resume/cancel operations.
 */
export class PBTCaseActionService {
  private readonly _getToken: () => Promise<string>;

  constructor(getToken: () => Promise<string>) {
    this._getToken = getToken;
  }

  async pauseCase(caseId: number, request: PauseCaseRequest): Promise<CaseActionResponse> {
    return this._postAction(PBTApiEndpoints.PauseCase(caseId), request);
  }

  async resumeCase(caseId: number): Promise<CaseActionResponse> {
    return this._postAction(PBTApiEndpoints.ResumeCase(caseId), undefined);
  }

  async cancelCase(caseId: number, request: CancelCaseRequest): Promise<CaseActionResponse> {
    return this._postAction(PBTApiEndpoints.CancelCase(caseId), request);
  }

  private async _postAction(url: string, body: unknown): Promise<CaseActionResponse> {
    const token = await this._getToken();
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: body ? JSON.stringify(body) : undefined,
    });

    if (!response.ok) {
      const errorJson = await response.json().catch(() => null);
      const errorMsg = errorJson?.error?.message || `API returned ${response.status}`;
      throw new Error(errorMsg);
    }

    const json = await response.json();
    const data = json?.data ?? json?.Data;
    return {
      case: data?.case ?? data?.Case ?? data,
      warnings: data?.warnings ?? data?.Warnings ?? [],
    };
  }
}
